/**
 * `dicePlayback` — the dice stage's **batch bookkeeping**, kept pure so it can be proven at all:
 * {@link ./DiceStage} needs a real WebGL context to construct, so nothing inside that class is
 * testable. (Same split as `throwProjection.ts` / `feedFreshness.ts` / `diceFade.ts`.)
 *
 * **One throw, one batch, one clock.** Each throw becomes its own batch with its own
 * settle→fade→reap timeline. A single ever-growing die list with one shared settle timestamp
 * can't express a Lancer **barrage** — two weapon attacks, two separate server round trips, two
 * throws: shot 2's dice pile onto shot 1's, shot 1's sit frozen at rest, unfaded and uncleared,
 * until shot 2 also settles, and only then does the whole pile fade out at once.
 *
 * The two rules that replace the pile, and why they differ:
 *  - a new throw **retires any already-settled batch** — those dice have been read, the next
 *    throw is what matters now, and letting them accumulate is the defect itself;
 *  - a new throw **leaves a still-tumbling batch alone** — yanking dice out of mid-air reads as a
 *    glitch, and the batch is only a fraction of a second from finishing on its own clock anyway.
 *
 * `payload` is opaque here (the stage stows its mesh handle in it) — this module never touches it,
 * it just hands it back on retire/reap so the owner can dispose the GPU resources.
 *
 * **Every batch carries an id**, and {@link advance} reports the batches that just settled by it.
 * That makes "shot 1's dice have stopped rolling" a distinct, addressable event from "the whole
 * stage went idle" ({@link isIdle}), which a barrage reaches only after BOTH shots have landed
 * and lingered out. The id is meaningless outside one {@link Playback} — it's a handle for
 * correlating a throw with its own settle, not a wire value.
 */
import { fadeAt } from "./diceFade";

/** One die being played back. `payload` is opaque here — the stage keeps its mesh in it. */
export type PlaybackDie<T> = {
  readonly payload: T;
  /** How many recorded frames this die has. */
  readonly frameCount: number;
  /** Index of the currently-shown recorded frame. */
  frameIndex: number;
  done: boolean;
};

/** One throw's dice, with the moment its last die landed. */
export type PlaybackBatch<T> = {
  /** This throw's handle, unique within its {@link Playback} — see the file header. */
  readonly id: number;
  dice: PlaybackDie<T>[];
  /** ms timestamp when this batch's last die finished; `null` while any die is still playing. */
  settledAt: number | null;
};

export type Playback<T> = {
  batches: PlaybackBatch<T>[];
  /** Whether anything has ever been thrown — an empty stage that never threw is not "idle". */
  spawned: boolean;
  /** Monotonic source of batch ids. Never reset — a retired batch's id is never handed out again. */
  nextBatchId: number;
};

export type BatchEntry<T> = { payload: T; frameCount: number };

/** What one {@link advance} produced: the dice that just landed, and the batches that just settled. */
export type AdvanceResult<T> = {
  /** Each die that finished on this step, reported exactly once. */
  finished: PlaybackDie<T>[];
  /** The ids of batches whose LAST die landed on this step, reported exactly once. */
  settled: number[];
};

export function createPlayback<T>(): Playback<T> {
  return { batches: [], spawned: false, nextBatchId: 0 };
}

/**
 * Add a throw's dice as a new batch. Any batch that has **already settled** is retired on the
 * spot — the next throw is what the table is showing now — and its payloads are returned for the
 * caller to dispose. A batch still tumbling keeps running on its own clock.
 *
 * Returns the new batch's `id` alongside the retired payloads. `id` is `null` for an empty
 * throw, which adds no batch at all.
 */
export function addBatch<T>(pb: Playback<T>, entries: readonly BatchEntry<T>[]): { id: number | null; retired: T[] } {
  if (entries.length === 0) return { id: null, retired: [] };
  pb.spawned = true;
  const retired: T[] = [];
  pb.batches = pb.batches.filter((batch) => {
    if (batch.settledAt === null) return true;
    for (const die of batch.dice) retired.push(die.payload);
    return false;
  });
  const id = pb.nextBatchId++;
  pb.batches.push({
    id,
    dice: entries.map((e) => ({ payload: e.payload, frameCount: e.frameCount, frameIndex: 0, done: false })),
    settledAt: null,
  });
  return { id, retired };
}

/**
 * Advance every still-playing die by `steps` recorded frames. Returns the dice that just finished
 * and the ids of the batches that just settled — both reported exactly once, on the step they
 * happened.
 */
export function advance<T>(pb: Playback<T>, steps: number, now: number): AdvanceResult<T> {
  const finished: PlaybackDie<T>[] = [];
  const settled: number[] = [];
  for (const batch of pb.batches) {
    for (const die of batch.dice) {
      if (die.done) continue;
      const next = die.frameIndex + steps;
      if (next >= die.frameCount) {
        die.frameIndex = die.frameCount - 1;
        die.done = true;
        finished.push(die);
      } else {
        die.frameIndex = next;
      }
    }
    // The batch's clock starts when its LAST die lands, and never restarts — an already-settled
    // batch keeps its original timestamp so its fade runs to completion. That "never restarts" is
    // also what makes `settled` a once-only report: the id is collected in the same branch.
    if (batch.settledAt === null && batch.dice.every((d) => d.done)) {
      batch.settledAt = now;
      settled.push(batch.id);
    }
  }
  return { finished, settled };
}

/** A batch's disappear-fade progress (0 = opaque, 1 = gone) at `now`. */
export function batchFade<T>(batch: PlaybackBatch<T>, now: number, lingerMs: number): number {
  if (batch.settledAt === null) return 0;
  return fadeAt(now - batch.settledAt, lingerMs);
}

/** Drop every batch whose linger has elapsed. Returns their payloads (the stage disposes them). */
export function reap<T>(pb: Playback<T>, now: number, lingerMs: number): T[] {
  const dropped: T[] = [];
  pb.batches = pb.batches.filter((batch) => {
    if (batch.settledAt !== null && now - batch.settledAt >= lingerMs) {
      for (const die of batch.dice) dropped.push(die.payload);
      return false;
    }
    return true;
  });
  return dropped;
}

/** Nothing left on the table — and something was there once. */
export function isIdle<T>(pb: Playback<T>): boolean {
  return pb.spawned && pb.batches.length === 0;
}
